import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, TrendingUp, TrendingDown, Landmark, FolderKanban,
  CheckSquare, BarChart3,
  ArrowRight, Hash, Loader2,
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { buscarGlobal, type ResultadoBusqueda } from '@/api/busqueda';
import { useProyectoStore } from '@/store/proyecto.store';
import { tieneAccesoAModulo } from '@utils/accesoModulos';
import { FS, useTema } from '@components/M02/casaTema';
import { NAV_ENTRADAS } from '@/utils/navegacion';
import { useTodosLosProyectos } from '@hooks/useProyectos';

// ─── Mapeo modulo del backend → ruta del router (App.tsx) ─────
// El endpoint devuelve filas de notificaciones; cada una trae `modulo`.
// Algunos módulos del backend tienen rutas con nombre distinto en el frontend.
// I-E (2026-09-07) — 'egresos' se quitó de aquí: ya no es una ruta plana, `rutaDeResultado()`
// lo resuelve aparte (necesita proyecto_id para armar /proyectos/:id).
const MODULO_A_RUTA: Record<string, string> = {
  ingresos:       '/ingresos',
  bancos:         '/bancos',
  // I-CRÉDITOS (2026-09-08, Tarea 8): 'creditos' se apagó como ruta plana — vive en la 8ª
  // pestaña de cada proyecto, igual que 'egresos' arriba.
  proyectos:      '/proyectos',
  tareas:         '/tareas',
  chats:          '/chat',
  ventas:         '/ventas',
  crm:            '/ventas',
  reportes:       '/reportes',
  notificaciones: '/notificaciones',
  configuracion:  '/configuracion',
  overhead:       '/overhead',
  contabilidad:   '/contabilidad',
  dashboard:      '/dashboard',
};

// Construye la ruta de destino de un resultado de búsqueda.
// Adjunta ?ref= con el referencia_id para que el módulo destino pueda enfocar el registro.
//
// I-E (2026-09-07) — «egresos» ya NO vive en una ruta plana: vive dentro de la casa de SU
// proyecto (`/proyectos/:id`, pestaña Pagos). Un resultado de búsqueda de un egreso SÍ trae
// `proyecto_id` (`egresos_registros.proyecto_id` es NOT NULL) — se usa para armar la ruta
// anidada; sin él (defensivo, no debería pasar) cae al selector de proyectos, nunca a una
// ruta muerta.
function rutaDeResultado(r: ResultadoBusqueda): string {
  // 🔴 2026-09-12 — `?tab=pagos`: la casa ya lee `?tab=` (I-xx); antes aterrizaba en Pagos
  // por ser el default de hoy, no porque se lo pidiera.
  if (r.modulo === 'egresos') {
    return r.proyecto_id ? `/proyectos/${r.proyecto_id}?tab=pagos&ref=${r.referencia_id}` : '/proyectos';
  }
  // I-251: Obra ya no tiene pantalla suelta; el resultado abre SU proyecto en la pestaña Obra.
  if (r.modulo === 'obra') {
    return r.proyecto_id ? `/proyectos/${r.proyecto_id}?tab=obra&ref=${r.referencia_id}` : '/proyectos';
  }
  const base = MODULO_A_RUTA[r.modulo] ?? '/dashboard';
  return `${base}?ref=${r.referencia_id}`;
}

// ─── Catálogo de acciones y rutas ────────────────────────────
interface CmdItem {
  id: string;
  label: string;
  description?: string;
  keywords: string[];
  icon: React.ReactNode;
  path: string;         // era: action: () => void — ahora datos puros sin closure
  category: string;
  modulo?: string; // undefined = siempre visible; string = filtra por usuario.modulos
  minNivel?: number; // candado de nivel de la ruta (sólo Tablero)
}

// ─── CommandPalette ───────────────────────────────────────────
interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const T = useTema();

  // I-47 (BUG-1149) — antes comparaba con su propia copia case-sensitive; ahora pasa por
  // la misma regla que la ruta, el menú y las pestañas (utils/accesoModulos.ts).
  const nivel = usuario?.nivel ?? 0;
  const modulosAsignados = usuario?.modulos ?? [];

  // useCallback: referencia estable — navigate es estable (React Router), onClose depende del padre
  const go = useCallback((path: string) => {
    navigate(path);
    onClose();
    setQuery('');
  }, [navigate, onClose]);

  /**
   * D-2026-09-07-F — `/egresos` se queda viva y es la vista de TODA LA EMPRESA; la casa
   * (`/proyectos/:id`) es la de UN proyecto. Con un proyecto conocido en el atajo global,
   * «Egresos»/«Nuevo egreso» entran a su casa; sin él, van a la vista de empresa (`/egresos`),
   * no al selector de proyectos.
   */
  const proyectoContextoId = useProyectoStore((s) => s.proyectoContextoId);
  // 🔴 2026-09-12 — `?tab=pagos`: mismo motivo que `rutaDeResultado` arriba.
  // 🔴 BUG-1613 (2026-09-14) — `/ingresos` NO es como `/egresos`: egresos sigue viva como
  // vista de empresa (D-2026-09-07-F), pero la pantalla de ingresos se APAGÓ el 11-sep (I-56)
  // y hasta hoy el buscador seguía ofreciendo «Ingresos» y su acción de cobro apuntando ahí.
  // Quien escribiera «cobro» y diera Enter aterrizaba en el Dashboard, sin una palabra.
  // Con proyecto de contexto entra DIRECTO a su pestaña Ingresos, sin rebote; sin contexto se
  // deja `/ingresos`, que ahora avisa y manda al selector (`RedirigirPantallaApagada`, App.tsx)
  // — ahí el aviso sí hace falta: explica por qué no llegó a una pantalla de ingresos.
  const TAB_DE_LA_CASA: Record<string, string> = { '/egresos': 'pagos', '/ingresos': 'ingresos' };
  const rutaDelItem = useCallback((item: CmdItem): string => {
    const tab = TAB_DE_LA_CASA[item.path];
    if (!tab) return item.path;
    return proyectoContextoId ? `/proyectos/${proyectoContextoId}?tab=${tab}` : item.path;
  }, [proyectoContextoId]);

  const ITEMS: CmdItem[] = useMemo(() => [
    // ── Navegación: sale de la lista ÚNICA (I-251), la misma del menú y el riel ──
    ...NAV_ENTRADAS.map((e): CmdItem => ({
      id: e.id, modulo: e.modulo ?? undefined, minNivel: e.minNivel, label: e.label, description: e.descripcion,
      keywords: e.keywords, icon: <e.icono size={15}/>, path: e.path, category: 'Pantallas',
    })),

    // ── Acciones rápidas (mismo modulo que el destino) ──
    { id: 'nuevo-ingreso',   modulo: 'ingresos',      label: 'Ir a Ingresos',            description: 'Ahí se captura un cobro',                  keywords: ['nuevo ingreso', 'crear ingreso', 'registrar pago', 'cobrar'],         icon: <TrendingUp size={15}/>,   path: '/ingresos',   category: 'Acciones' },
    { id: 'nuevo-egreso',    modulo: 'egresos',       label: 'Nuevo egreso',          description: 'Registrar un egreso',                 keywords: ['nuevo egreso', 'crear gasto', 'pagar', 'proveedor', 'factura'],       icon: <TrendingDown size={15}/>, path: '/egresos',    category: 'Acciones' },
    // 🔴 QUITADO 2026-09-12 (Mau, «APAGA LA PANTALLA VIEJA»): «Nuevo prospecto» abría
    // `/ventas` → `NuevoProspectoForm`. Se cotiza desde la unidad (FichaDeUnidad.tsx →
    // Cotizar → CotizadorUnificado con `permiteBuscarProspecto`, que da de alta ahí mismo).
    { id: 'cerrar-mes',      modulo: 'bancos',        label: 'Cierre de período',     description: 'Bancos → Cierre de mes contable',     keywords: ['cerrar mes', 'cierre periodo', 'cierre contable', 'periodo', 'mes'],  icon: <Landmark size={15}/>,     path: '/bancos',     category: 'Acciones' },
    { id: 'conciliacion',    modulo: 'bancos',        label: 'Conciliación bancaria', description: 'Conciliar movimientos',               keywords: ['conciliacion', 'conciliar', 'banco', 'estados cuenta', 'mt940'],      icon: <Landmark size={15}/>,     path: '/bancos',     category: 'Acciones' },
    { id: 'nueva-tarea',     modulo: 'tareas',        label: 'Nueva tarea',           description: 'Crear tarea para el equipo',          keywords: ['nueva tarea', 'asignar', 'pendiente', 'recordatorio'],                icon: <CheckSquare size={15}/>,  path: '/tareas',     category: 'Acciones' },
    { id: 'reporte-mensual', modulo: 'reportes',      label: 'Reporte mensual',       description: 'Generar reporte ejecutivo del mes',   keywords: ['reporte mensual', 'informe mensual', 'ejecutivo', 'descargar pdf'],   icon: <BarChart3 size={15}/>,    path: '/reportes',   category: 'Acciones' },
  ], []);
  // Sin eslint-disable: ITEMS no captura funciones, deps [] es correcto

  // I-251 (README:112-120): la paleta trae TRES grupos — Pantallas · Proyectos · Acciones. Los proyectos salen de la
  // misma lista que el selector y «Cambiar» (`useTodosLosProyectos`), y sólo se ofrecen cuando se escribe algo.
  // `enabled: isOpen`: la paleta vive montada en la cabecera de TODA pantalla; la lista sólo se pide cuando se abre.
  const { data: listaProyectos } = useTodosLosProyectos({ enabled: isOpen });
  const ITEMS_TODOS: CmdItem[] = useMemo(() => {
    const proyectos: CmdItem[] = (listaProyectos?.data ?? []).map((p) => ({
      id: `proyecto-${p.id}`, modulo: 'proyectos', label: p.nombre,
      description: [p.clave, p.ubicacion].filter(Boolean).join(' · ') || undefined,
      keywords: [p.clave ?? '', p.ubicacion ?? ''], icon: <FolderKanban size={15}/>, path: `/proyectos/${p.id}`, category: 'Proyectos',
    }));
    const i = ITEMS.findIndex((x) => x.category === 'Acciones');
    return [...ITEMS.slice(0, i), ...proyectos, ...ITEMS.slice(i)];
  }, [ITEMS, listaProyectos]);

  // Filtrar por módulo primero, luego por query de búsqueda
  const results = useMemo(() => {
    const visibles = ITEMS_TODOS.filter((item) => (item.minNivel == null || nivel >= item.minNivel) && (!item.modulo || tieneAccesoAModulo(modulosAsignados, item.modulo, nivel, usuario?.accesos)));

    // Los proyectos sólo se ofrecen cuando se escribe algo (con 60 sería una pared), y a lo más 6.
    if (!query.trim()) return visibles.filter((item) => item.category !== 'Proyectos');
    const q = query.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    let proyectosVistos = 0;
    return visibles.filter((item) => {
      const searchable = [item.label, item.description ?? '', ...item.keywords]
        .join(' ')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
      if (!searchable.includes(q)) return false;
      return item.category !== 'Proyectos' || ++proyectosVistos <= 6;
    });
  }, [query, ITEMS_TODOS, modulosAsignados, nivel, usuario?.accesos]);

  // Agrupar por categoría
  const grouped = useMemo(() => {
    const map: Record<string, CmdItem[]> = {};
    results.forEach((item) => {
      if (!map[item.category]) map[item.category] = [];
      map[item.category].push(item);
    });
    return Object.entries(map);
  }, [results]);

  // ─── Búsqueda global real (G-02) ────────────────────────────
  // Debounce ~250ms del texto antes de pegarle al endpoint.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const busquedaActiva = debouncedQuery.length >= 2;
  const {
    data: resultadosBusqueda = [],
    isFetching: buscando,
    isError: errorBusqueda,
  } = useQuery({
    queryKey: ['busqueda', debouncedQuery],
    queryFn: () => buscarGlobal(debouncedQuery),
    enabled: busquedaActiva,
    staleTime: 30_000,
  });

  // Agrupar los resultados reales por módulo
  const resultadosAgrupados = useMemo(() => {
    const map: Record<string, ResultadoBusqueda[]> = {};
    (resultadosBusqueda as ResultadoBusqueda[]).forEach((r) => {
      const key = r.modulo || 'otros';
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map);
  }, [resultadosBusqueda]);

  // Etiqueta legible por módulo para los encabezados de la sección de resultados
  const labelModulo = useCallback((modulo: string): string => {
    const item = ITEMS.find((i) => i.modulo === modulo);
    if (item) return item.label;
    return modulo.charAt(0).toUpperCase() + modulo.slice(1);
  }, [ITEMS]);

  // Foco automático y Escape
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ⌘K global
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) return; // se maneja desde fuera
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed z-[60] rounded-2xl overflow-hidden"
        style={{
          background: T.panelSolid,
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 560,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5"
          style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
          <Search size={16} style={{ color: T.fg3, flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar módulo, acción o función..."
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: T.fg }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-xs px-2 py-0.5 rounded-md"
              style={{ backgroundColor: T.chip, color: T.fg2 }}
            >
              ✕
            </button>
          )}
          <span
            className="text-xs px-2 py-0.5 rounded-md flex-shrink-0"
            style={{ backgroundColor: T.chip, color: T.fg3, fontSize: FS.chip }}
          >
            ESC
          </span>
        </div>

        {/* Resultados */}
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {grouped.length === 0 && resultadosAgrupados.length === 0 && !buscando ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <Hash size={24} style={{ color: T.fg3 }} />
              <p className="text-sm" style={{ color: T.fg3 }}>Sin resultados para &quot;{query}&quot;</p>
            </div>
          ) : (
            grouped.map(([category, items]) => (
              <div key={category}>
                <div className="px-4 py-2"
                  style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                  <p className="text-[10px] font-bold uppercase tracking-widest"
                    style={{ color: T.fg3 }}>
                    {category}
                  </p>
                </div>
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => go(rutaDelItem(item))}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                    style={{ borderBottom: `1px solid ${T.lineSoft}` }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = '';
                    }}
                  >
                    <div className="flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ width: 30, height: 30, backgroundColor: T.chip, color: T.fg3 }}>
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: T.fg }}>{item.label}</p>
                      {item.description && (
                        <p className="text-xs" style={{ color: T.fg3 }}>{item.description}</p>
                      )}
                    </div>
                    <ArrowRight size={13} style={{ color: T.fg3, flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            ))
          )}

          {/* ── Búsqueda global (G-02) ── */}
          {busquedaActiva && buscando && resultadosAgrupados.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-4">
              <Loader2 size={14} className="animate-spin" style={{ color: T.fg3 }} />
              <p className="text-xs" style={{ color: T.fg3 }}>Buscando en el sistema…</p>
            </div>
          )}

          {busquedaActiva && !buscando && errorBusqueda && (
            <div className="px-4 py-3" style={{ borderTop: `1px solid ${T.lineSoft}` }}>
              <p className="text-xs" style={{ color: T.fg3 }}>
                No se pudo completar la búsqueda global. Intenta de nuevo.
              </p>
            </div>
          )}

          {busquedaActiva && resultadosAgrupados.length > 0 && (
            resultadosAgrupados.map(([modulo, items]) => (
              <div key={`busqueda-${modulo}`}>
                <div className="px-4 py-2" style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                  <p className="text-[10px] font-bold uppercase tracking-widest"
                    style={{ color: T.fg3 }}>
                    Resultados · {labelModulo(modulo)}
                  </p>
                </div>
                {items.map((r) => (
                  <button
                    key={`res-${r.id}`}
                    onClick={() => go(rutaDeResultado(r))}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                    style={{ borderBottom: `1px solid ${T.lineSoft}` }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = '';
                    }}
                  >
                    <div className="flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ width: 30, height: 30, backgroundColor: T.chip, color: T.fg3 }}>
                      <Search size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: T.fg }}>{r.mensaje}</p>
                      <p className="text-xs" style={{ color: T.fg3 }}>{labelModulo(r.modulo)}</p>
                    </div>
                    <ArrowRight size={13} style={{ color: T.fg3, flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {/* README:120 — por qué la paleta no ofrece lo que el menú no ofrece */}
        <p className="px-4 pt-2 text-[10px]" style={{ color: T.fg3, backgroundColor: T.panelSoft, borderTop: `1px solid ${T.lineSoft}` }}>
          Sale de la misma lista que el menú y el riel: lo que no puedes abrir tampoco aparece aquí.
        </p>
        <div className="flex items-center gap-4 px-4 py-2.5"
          style={{ borderTop: `1px solid ${T.lineSoft}`, backgroundColor: T.panelSoft }}>
          <p className="text-[10px]" style={{ color: T.fg3 }}>
            <kbd className="px-1.5 py-0.5 rounded text-[9px] mr-1"
              style={{ backgroundColor: T.chip, color: T.fg3, border: `1px solid ${T.line}` }}>↑↓</kbd>
            navegar
          </p>
          <p className="text-[10px]" style={{ color: T.fg3 }}>
            <kbd className="px-1.5 py-0.5 rounded text-[9px] mr-1"
              style={{ backgroundColor: T.chip, color: T.fg3, border: `1px solid ${T.line}` }}>↵</kbd>
            ir
          </p>
          <p className="text-[10px]" style={{ color: T.fg3 }}>
            <kbd className="px-1.5 py-0.5 rounded text-[9px] mr-1"
              style={{ backgroundColor: T.chip, color: T.fg3, border: `1px solid ${T.line}` }}>⌘K</kbd>
            abrir/cerrar
          </p>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
